import { ref, watch, computed } from 'vue';

// Available themes with metadata
export const themes = [
  {
    id: 'light',
    name: 'Light',
    icon: 'fa-sun',
    colors: {
      primary: '#36a2eb',
      background: '#F6F5F3',
      card: '#ffffff',
      text: '#212529',
      border: '#dee2e6'
    }
  },
  {
    id: 'dark',
    name: 'Dark',
    icon: 'fa-moon',
    colors: {
      primary: '#36a2eb',
      background: '#121212',
      card: '#1e1e1e',
      text: '#ffffff',
      border: '#404040'
    }
  },
  {
    id: 'dracula',
    name: 'Dracula',
    icon: 'fa-dragon',
    colors: {
      primary: '#98afff',
      background: '#15161c',
      card: '#282a36',
      text: '#ffffff',
      border: '#323881'
    }
  },
  {
    id: 'sunset',
    name: 'Warm Sunset',
    icon: 'fa-cloud-sun',
    colors: {
      primary: '#e67e50',
      background: '#fef6f0',
      card: '#ffffff',
      text: '#3d2817',
      border: '#f4d4ba'
    }
  },
  {
    id: 'rainbow',
    name: 'Rainbow',
    icon: 'fa-rainbow',
    colors: {
      primary: '#8338ec',
      background: '#1a1a2e',
      card: '#16213e',
      text: '#ffffff',
      border: '#0f4c75'
    }
  },
  {
    id: 'forest',
    name: 'Forest',
    icon: 'fa-tree',
    colors: {
      primary: '#5a8f69',
      background: '#f4f7f0',
      card: '#ffffff',
      text: '#2d4a2b',
      border: '#c8dcc4'
    }
  },
  {
    id: 'pastel',
    name: 'Pastel',
    icon: 'fa-palette',
    colors: {
      primary: '#c5b3e6',
      background: '#fef9f3',
      card: '#ffffff',
      text: '#4a4a4a',
      border: '#e8d5e8'
    }
  }
];

// Singleton state - shared across all component instances
const currentTheme = ref('light');
let isInitialized = false;

export function useDarkMode() {
  // Initialize only once
  if (!isInitialized) {
    // Load saved preference from localStorage
    const savedTheme = localStorage.getItem('theme') || 'light';
    currentTheme.value = savedTheme;
    
    // Apply initial theme
    applyTheme(savedTheme);
    
    // Watch for changes and persist to localStorage
    watch(currentTheme, (newValue) => {
      localStorage.setItem('theme', newValue);
      applyTheme(newValue);
    });
    
    isInitialized = true;
  }

  const isDarkMode = computed(() => currentTheme.value !== 'light');

  const toggleDarkMode = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light';
  };

  const setTheme = (themeId) => {
    if (themes.find(t => t.id === themeId)) {
      currentTheme.value = themeId;
    }
  };

  const setDarkMode = (value) => {
    currentTheme.value = value ? 'dark' : 'light';
  };

  return {
    currentTheme,
    isDarkMode,
    toggleDarkMode,
    setTheme,
    setDarkMode,
    themes
  };
}

function applyTheme(themeId) {
  // Remove old classes for backward compatibility
  document.body.classList.remove('dark-mode-body', 'dark-mode-body-2');
  
  // Always set data-theme attribute for consistency
  document.body.setAttribute('data-theme', themeId);

  // Update browser chrome theme-color for Android Chrome / PWA
  const theme = themes.find(t => t.id === themeId);
  if (theme) {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', theme.colors.background);
  }
}
